import {
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { PeselUtils } from '../utils/pesel.utils';

export class SharedValidators {
  private static readonly DO_NOT_BEGIN_WITH_WHITESPACE_PATTERN = /^\S.*$/;
  private static readonly EMAIL_PATTERN =
    /^([0-9a-zA-Z]+[\-._+&amp;])*[0-9a-zA-Z]+@([\-0-9a-zA-Z]+[.])+[a-zA-Z]{2,6}$/;
  private static readonly PHONE_NUMBER_PATTERN =
    /[\(]?[+]?(\d{2,3})?[\)]?[ -]?\d{2,3}[ -]?\d{2,3}[ -]?\d{2,3}$/;
  private static readonly PHONE_NUMBER_EXIST_PATTERN = /^[0-9+ ]+$/;
  private static readonly IMIE_NAZWISKO_PATTERN =
    /^[^0-9^${}()*.+?|<>;,&\/@#%~"_;:!°]+$/;
  public static readonly DANE_OSOBOWE_PATTERN =
    /^[A-Za-z.\-\'ąćęłńóśźżĄĆĘŁŃÓŚŹŻĽŠŞŤŹŽŔÁÂĂÄĹÇČÉËĚÍÎĎĐŇÔŐÖŘŮÚŰÜÝŢÜľšşťźžŕáâăäĺçčéëěíîďđňôőöřůúűüýţüß ]*$/;

  /**
   * Walidator, który sprawdza, czy którakolwiek z podanych kontrolek ma wartość.
   * Zwraca błąd o podanej nazwie, jeśli wszystkie są puste.
   * @param errorName - nazwa błędu do zwrócenia (np. 'requiredAny')
   * @param controls - lista kontrolek do sprawdzenia
   */
  public static anyControlHasValueValidator(
    errorName: string,
    ...controls: AbstractControl[]
  ): ValidatorFn {
    return (fg: AbstractControl): ValidationErrors | null => {
      const hasValue = controls.some((control) => {
        const val = control?.value;
        return val !== null && val !== undefined && val !== '';
      });
      return hasValue ? null : { [errorName]: true };
    };
  }

  /**
   * Walidator warunkowy — stosuje podany walidator tylko wtedy,
   * gdy funkcja warunku (`conditionFunction`) zwróci `true`.
   *
   * Umożliwia dynamiczne włączanie lub wyłączanie walidacji w zależności od innych pól formularza.
   *
   * @param validator - walidator, który ma zostać uruchomiony, gdy warunek zostanie spełniony
   * @param conditionFunction - funkcja zwracająca `true` lub `false`, określająca, kiedy walidator ma być aktywny
   * @returns ValidatorFn - funkcja walidująca, która zwraca błąd, jeśli walidator zwróci błąd i warunek jest spełniony, w przeciwnym razie `null`
   *
   * @example
   * // Przykład: pole "nrDokumentu" jest wymagane tylko jeśli wybrano "rodzajDokumentu"
   * control.addValidators(
   *   SharedValidators.when(
   *     Validators.required,
   *     () => !!form.get('rodzajDokumentu')?.value
   *   )
   * );
   */
  static when(
    validator: ValidatorFn,
    conditionFunction: () => boolean,
  ): ValidatorFn {
    return function (control: AbstractControl): ValidationErrors | null {
      if (conditionFunction()) {
        return validator(control);
      }
      return null;
    };
  }

  static requiredWithCustomMessage(errorName: string): ValidatorFn {
    const base = Validators.required;
    return (control: AbstractControl): ValidationErrors | null => {
      const result = base(control);
      return result ? { [errorName]: true } : null;
    };
  }

  static requiredTextWhichIsNotOnlyEmptyHtml(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;

      if (value == null) {
        return { required: true };
      }

      if (typeof value !== 'string') {
        return null;
      }

      const text = value
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .trim();

      return text.length === 0 ? { required: true } : null;
    };
  }

  static patternWithCustomMessage(
    pattern: RegExp,
    errorName: string,
  ): ValidatorFn {
    const base = Validators.pattern(pattern);
    return (control: AbstractControl): ValidationErrors | null => {
      const result = base(control);
      return result ? { [errorName]: true } : null;
    };
  }

  static maxLengthWithCustomMessage(
    max: number,
    errorName: string,
  ): ValidatorFn {
    const base = Validators.maxLength(max);
    return (control: AbstractControl): ValidationErrors | null => {
      const result = base(control);
      return result ? { [errorName]: true } : null;
    };
  }

  static doNotBeginWithWhitespace(): ValidatorFn {
    return SharedValidators.patternWithCustomMessage(
      SharedValidators.DO_NOT_BEGIN_WITH_WHITESPACE_PATTERN,
      'doNotBeginWithWhitespace',
    );
  }

  static imieNazwiskoValidator(): ValidatorFn {
    return SharedValidators.patternWithCustomMessage(
      SharedValidators.IMIE_NAZWISKO_PATTERN,
      'invalidImieNazwisko',
    );
  }

  static emailValidator(): ValidatorFn {
    // Regex na email zaczerpniety z XSD (CRWDE)
    return SharedValidators.patternWithCustomMessage(
      SharedValidators.EMAIL_PATTERN,
      'invalidEmail',
    );
  }

  static peselValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }

      return PeselUtils.decode(control.value).isValid
        ? null
        : { invalidPesel: true };
    };
  }

  static phoneExistsValidator(): ValidatorFn {
    return SharedValidators.patternWithCustomMessage(
      SharedValidators.PHONE_NUMBER_EXIST_PATTERN,
      'providePhoneNumber',
    );
  }

  static phoneNumberPatternValidator(): ValidatorFn {
    return SharedValidators.patternWithCustomMessage(
      SharedValidators.PHONE_NUMBER_PATTERN,
      'incorrectPhoneNumber',
    );
  }

  static daneOsobowePatternValidator(): ValidatorFn {
    return SharedValidators.patternWithCustomMessage(
      SharedValidators.DANE_OSOBOWE_PATTERN,
      'incorrectPattern',
    );
  }
}
